/**
 * Soft session-expiry notification.
 *
 * Historically a dead refresh token meant an immediate window.location.replace("/login...") —
 * a hard navigation that instantly destroys any in-progress chat state (streaming response,
 * pending local drafts still in memory, retry timers). AuthProvider registers a handler here
 * once it mounts; from then on, session-expiry flows through this soft path (show an in-app
 * "please sign in again" prompt, keep the page mounted) instead of navigating away. Callers that
 * fire before AuthProvider mounts (or when no handler is registered) fall back to the old hard
 * redirect via the caller's own logic.
 */

type Listener = () => void;

let listener: Listener | null = null;

/** Called once by AuthProvider on mount. Returns an unsubscribe function. */
export function registerSessionExpiredHandler(fn: Listener): () => void {
  listener = fn;
  return () => {
    if (listener === fn) listener = null;
  };
}

/** Returns true if a handler was registered and notified (soft path taken), false otherwise. */
export function notifySessionExpired(): boolean {
  if (listener) {
    listener();
    return true;
  }
  return false;
}

/** Dispatched after a successful re-auth so background retry loops (pending thread sync, etc.)
 * can resume immediately instead of waiting for their next poll interval. */
export const SESSION_RESTORED_EVENT = "ds:session-restored";

export function notifySessionRestored(): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(SESSION_RESTORED_EVENT));
  }
}
